<script lang="ts">
  import { run } from "svelte/legacy";

  import Card from "../../components/atoms/Card.svelte";
  import { useMutation } from "@sveltestack/svelte-query";
  import type { SignStartResult, SignStatusTypes, Summary, TSign } from "../../helpers/types";
  import { Products, useCancelSigning } from "../../helpers/api";
  import SelectedProduct from "../../components/organisms/SelectedProduct.svelte";
  import Loader from "../../components/atoms/Loader.svelte";
  import Heading from "../../components/atoms/Heading.svelte";
  import Button from "../../components/atoms/Button.svelte";
  import Checkbox from "../../components/atoms/Checkbox.svelte";
  import { onMount } from "svelte";
  import { GTMAdtraction, GTMPageView } from "../../helpers/tracking";
  import {
    checkDeviceType,
    getSummary,
    readCookie,
    toggleHTMLElement,
  } from "../../helpers/helperMethods";
  import AnimatedBox from "../../components/atoms/AnimatedBox.svelte";
  import useMovingFlow from "../../providers/useMovingFlow";
  import BankIdSignError from "../../components/molecules/BankIdSignError.svelte";
  import SignError from "../../components/molecules/SignError.svelte";
  import PersonalSummary from "../../components/molecules/PersonalSummary.svelte";
  import Text from "../../components/atoms/Text.svelte";
  import BankIdSign from "../../components/organisms/BankIdSign.svelte";
  import { writable } from "svelte/store";
  import ErrorBanner from "../../components/atoms/ErrorBanner.svelte";

  interface Props {
    seller: string;
  }

  let { seller }: Props = $props();

  const signStatus = writable<SignStatusTypes>("IDLE");
  const signData = writable<SignStartResult | undefined>(undefined);
  const signWithQRCode = writable(checkDeviceType() === "Desktop" ? true : false);

  const { state: store, send } = useMovingFlow();

  const { person, facility, invoice } = $store.context;

  let errorMessages: string[] = $state([]);
  let facilityError: boolean = false;
  let signId: number;
  let productOfferId = $state($store.context.productOfferId);
  let useBankId = $state(false);
  let btn: HTMLButtonElement;
  let breadCrumbs: HTMLDivElement;
  let showApproveTermsError = false;

  onMount(() => {
    btn = document.getElementById("back-button") as HTMLButtonElement;
    breadCrumbs = document.getElementById("breadcrumbs-container") as HTMLDivElement;
  });

  const signMutation = useMutation<TSign, any, any>(
    (data: Summary) =>
      Products.signProduct({
        data,
        bankId: useBankId,
        signData,
        signWithQRCode,
        signStatus,
      }),
    {
      retry: () => false,
      onError: (data: any) => {
        toggleHTMLElement(btn);
        toggleHTMLElement(breadCrumbs);
        GTMPageView("page-order-failed");
        facilityError = false;
        errorMessages = [];
        if (data.result) {
          for (var property in data.result) {
            if (property.toLowerCase().includes("facility")) {
              facilityError = true;
            }
            errorMessages.push(data.result[property]);
          }
        }
      },

      onSuccess: (data) => {
        GTMPageView("page-order-success");
        localStorage.removeItem("purchaseValues");
        facilityError = false;
        errorMessages = [];
        if (data.result && data.result.sign) {
          signId = data?.result?.sign?.id;
          const code = readCookie("at_gd");
          if (code) GTMAdtraction(signId, code);
          let event = new CustomEvent("confirmationpage", {
            detail: {
              avtalsid: data.result.sign.id,
              kampanj: $store.context.campaign,
            },
          });
          document.dispatchEvent(event);
        } else {
          console.log("no signature id found in response");
        }
        send("SET_SIGN_ID", { signId: signId });
        send("NEXT");
      },
    }
  );

  const toggleAddonProduct = (id: number) => {
    const index = summary.additionalProduct.indexOf(id);
    if (index == -1) {
      summary.additionalProduct.push(id);
    } else {
      summary.additionalProduct.splice(index, 1);
    }
    summary.additionalProduct = summary.additionalProduct;
    send("SET_ADDITIONAL_PRODUCTS", { additionalProducts: summary.additionalProduct });
  };

  let summary = $state(getSummary($store.context, seller, invoice.value, facility, true));

  $effect(() => {
    if (productOfferId) summary.id = productOfferId;
  });

  $effect(() => {
    if ($signMutation.isError && !useBankId) window.scrollTo({ top: 0, behavior: "smooth" });
  });

  const cancelSigningMutation = useCancelSigning();

  const signProduct = () => {
    if (!summary.terms) {
      showApproveTermsError = true;
      return;
    }
    signStatus.set("STARTINGSIGN");
    $signMutation.mutate(summary);
    toggleHTMLElement(btn);
    toggleHTMLElement(breadCrumbs);
    GTMPageView("page-order-sent");
  };

  const cancelSigning = () => {
    signStatus.set("STOP");
    if ($signData) {
      $cancelSigningMutation.mutate($signData);
    }
    signData.set(undefined);
    $signMutation.reset();
  };
</script>

{#if (!useBankId && !$signMutation.isSuccess) || $signMutation.isError || $signMutation.isIdle}
  {#if $signMutation.isError}
    {#if $signMutation.error.signError}
      <BankIdSignError />
    {:else}
      <SignError errorMessages={errorMessages ? errorMessages : []} />
    {/if}
  {/if}
  <AnimatedBox>
    {#if seller}
      <Card>
        <Heading margin={false} variant={"secondary"}>
          Säljare: {seller}
        </Heading>
      </Card>
    {/if}
    <SelectedProduct
      hasFetchedAutomatic={$store.context.hasFetchedPersonAutomatic}
      bind:useBankId
      zipcode={$store.context.facilityInformation.zipcode}
      campaign={$store.context.campaign}
      selectedProductId={$store.context.selectedProductId}
      estimation={$store.context.estimation}
      changeProduct={() => send("PRICE")}
      isLoading={$signMutation.isLoading}
      selectedAddons={summary.additionalProduct}
      {toggleAddonProduct}
      bind:productOfferId
    />
    <PersonalSummary
      {person}
      pin={summary.pin}
      email={summary.email}
      onChange={() => send("INFORMATION")}
      phone={summary.phone}
      isLoading={$signMutation.isLoading}
      invoiceDescription={invoice.description}
      invoiceValue={summary.invoiceDistValue}
      authority={summary.authority}
      areaId={summary.areaId}
      facilityId={summary.facilityId}
      startDate={summary.startDate}
      facilityAddress={summary.deliveryAddress}
      facilityZipcode={summary.deliveryZipcode.toString()}
      facilityCity={summary.deliveryCity}
      apartmentNumber={summary.apartmentNumber}
      formerResidence={summary.formerResidence}
      propertyOwner={summary.propertyOwner}
    />
    {#if $signMutation.isLoading}
      <div class="mt-8 w-full flex justify-center">
        <Loader />
      </div>
    {:else}
      <div class="text-xs sm:text-base">
        <Checkbox
          onChange={() => {
            summary.terms = !summary.terms;
            if (summary.terms) showApproveTermsError = false;
          }}
          checked={summary.terms}
          required={true}
        >
          Jag godkänner avtalsvillkoren
        </Checkbox>
        <span class="block mt-2"
          >Läs våra
          <a
            href="https://www.skekraft.se/avtalsvillkor_konsument/"
            target="_blank"
            rel="noopener"
            class="text-Violet700 hover:underline"
            >Allmänna
          </a>
          och
          <a
            href="https://www.skekraft.se/sarskilda_avtalsvillkor_konsument/"
            target="_blank"
            rel="noopener"
            class="text-Violet700 hover:underline"
            >särskilda villkor,
          </a>
          eller ta del av en
          <a
            href="https://www.skekraft.se/sammanfattning"
            target="_blank"
            rel="noopener"
            class="text-Violet700 hover:underline">sammanfattning.</a
          >
          <br />
          <a
            href="https://www.skekraft.se/om-oss/personuppgifter/"
            class="text-Violet700 hover:underline"
            target="_blank">Läs mer om hur vi hanterar dina personuppgifter.</a
          >
        </span>
        <Text class="mt-2" variant="primary"
          >Jag har läst och förstått avtalsvillkoren. I avtalsvillkoren finns bland annat
          information om ångerrätt och lagring av dina personuppgifter</Text
        >
      </div>
      <div class="h-auto">
        {#if showApproveTermsError}
          <ErrorBanner text="Du måste godkänna avtalsvillkoren" />
        {/if}
        <Button
          animate={false}
          disabled={$store.context.purchaseFlowType === "v1"
            ? !productOfferId || !summary.terms
            : false}
          type="button"
          onClick={signProduct}
          class="w-full my-4">{useBankId ? "Teckna med bank-id" : "Beställ"}</Button
        >
      </div>
    {/if}
  </AnimatedBox>
{:else if useBankId && $signMutation.isLoading}
  <AnimatedBox>
    <BankIdSign
      bind:signWithQRCode={$signWithQRCode}
      {cancelSigning}
      signData={$signData}
      changeSignDevice={() => signWithQRCode.update((isQR) => !isQR)}
      isLoading={$signStatus === "STARTINGSIGN"}
    />
  </AnimatedBox>
{/if}
